import type { ColumnDef } from "@tanstack/react-table"
import { addHours, format } from "date-fns"
import type { TFunction } from "i18next"
import {
  CopyIcon,
  ExternalLinkIcon,
  UserIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { FRONTEND_ROUTES } from "@/constants"
import { getPaymentMethod } from "@/helpers/defaulters/get-payment-method"
import {
  formatDocument,
  formatPhoneNumber,
} from "@/helpers/format"
import { toast } from "@/hooks/use-toast"

interface CustomerCellProps {
  name?: string
  document?: string
  email?: string
  phone?: string
}

const CustomerIdCell = ({ ownId }: { ownId: string }) => {
  const { t } = useTranslation()

  const handleCopy = () => {
    navigator.clipboard.writeText(ownId)
    toast({
      title: t("orders.table.orderIdCopied"),
      variant: "success",
    })
  }

  return (
    <div className="flex items-center gap-1">
      <span>{ownId}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleCopy}
            className="rounded p-1 hover:bg-muted"
          >
            <CopyIcon
              size={12}
              className="text-muted-foreground"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>{t("common.copy")}</TooltipContent>
      </Tooltip>
    </div>
  )
}

const CustomerCell = ({
  name,
  document,
  email,
  phone,
}: CustomerCellProps) => {
  const { t } = useTranslation()

  const handleCopy = (text: string, message: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: message, variant: "success" })
  }

  return (
    <div className="flex items-start gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <UserIcon size={14} className="text-primary" />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="truncate font-medium">
          {name || "-"}
        </p>
        {document && (
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground">
              {formatDocument(document)}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() =>
                    handleCopy(
                      document,
                      t("orders.table.documentCopied")
                    )
                  }
                  className="rounded p-0.5 hover:bg-muted"
                >
                  <CopyIcon
                    size={10}
                    className="text-muted-foreground"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t("common.copy")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-1">
            <p
              className="max-w-[160px] truncate text-xs text-muted-foreground"
              title={email}
            >
              {email}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() =>
                    handleCopy(
                      email,
                      t("orders.table.emailCopied")
                    )
                  }
                  className="rounded p-0.5 hover:bg-muted"
                >
                  <CopyIcon
                    size={10}
                    className="text-muted-foreground"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t("common.copy")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        {phone && (
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground">
              {formatPhoneNumber(phone)}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() =>
                    handleCopy(
                      phone,
                      t("orders.table.phoneCopied")
                    )
                  }
                  className="rounded p-0.5 hover:bg-muted"
                >
                  <CopyIcon
                    size={10}
                    className="text-muted-foreground"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {t("common.copy")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  )
}
export const createDefaultersOrderColumns = (
  t: TFunction
): ColumnDef<DefaulterOrder>[] => [
  {
    accessorKey: "ownId",
    header: "ID do cliente",
    cell: ({ row }) => (
      <CustomerIdCell ownId={row.original.ownId} />
    ),
  },
  {
    accessorKey: "code",
    header: "Código",
    cell: ({ row }) => <span>{row.original.code}</span>,
  },
  {
    accessorKey: "customer",
    header: "Cliente",
    enableHiding: false,
    cell: ({ row }) => {
      const { name, document, email, phone } = row.original
      return (
        <CustomerCell
          name={name}
          document={document}
          email={email}
          phone={phone}
        />
      )
    },
  },
  {
    accessorKey: "insuranceType",
    header: "Tipo de seguro",
    cell: ({ row }) => (
      <span>{row.original.insuranceType}</span>
    ),
  },
  {
    accessorKey: "paymentMethod",
    header: "Método de pagamento",
    cell: ({ row }) => {
      const { paymentMethod } = row.original
      return (
        <span>
          {paymentMethod
            ? getPaymentMethod(paymentMethod, t)
            : "-"}
        </span>
      )
    },
  },
  {
    accessorKey: "creationDate",
    header: "Data de criação",
    cell: ({ row }) => (
      <span>
        {format(
          addHours(new Date(row.original.creationDate), 3),
          "dd/MM/yyyy"
        )}
      </span>
    ),
  },
  {
    accessorKey: "orderExpirationDate",
    header: "Data de expiração",
    cell: ({ row }) => (
      <span>
        {format(
          addHours(
            new Date(row.original.orderExpirationDate),
            3
          ),
          "dd/MM/yyyy"
        )}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Situação",
    cell: ({ row }) => <span>{row.original.status}</span>,
  },
  {
    accessorKey: "link",
    header: "Link de pagamento",
    cell: () => <div className="text-center">-</div>,
  },
  {
    accessorKey: "actions",
    header: "",
    enableHiding: false,
    cell: ({ row }) => {
      const { id, code } = row.original
      return (
        <Button variant="ghost" asChild size={"icon"}>
          <Link
            to={`${FRONTEND_ROUTES.ORDERS.href}/details/${id}/${code}`}
          >
            <span className="sr-only">Detalhes</span>
            <ExternalLinkIcon
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
          </Link>
        </Button>
      )
    },
  },
]
